import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../auth/AuthContext';
import {
  ApiError,
  CustomerAddress,
  CustomerDetail,
  CustomerPointsResponse,
  CustomerRow,
  CustomersPage as CustomersPageData,
  PointsLedgerEntry,
  getCustomer,
  getCustomerPoints,
  listCustomers,
} from '../../api';

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const fmtDateTime = (s: string) => new Date(s).toLocaleString();
const fmtMoney = (s: string) => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : `$${s}`;
};

const CustomersPage = () => {
  const { idToken } = useAuth();

  // Free-text search; committed via Enter or the Apply button so we don't
  // hammer the backend on every keystroke. Empty = no filter.
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [data, setData] = useState<CustomersPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string>('');
  const [pointsDetail, setPointsDetail] = useState<CustomerPointsResponse | null>(null);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    listCustomers(idToken, search, rowsPerPage, page * rowsPerPage)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `API error ${err.status}`
            : (err as Error)?.message || 'fetch failed',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, search, page, rowsPerPage]);

  const applySearch = () => {
    const t = searchDraft.trim();
    setSearch(t === '' ? null : t);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchDraft('');
    setSearch(null);
    setPage(0);
  };

  const openDetail = async (id: string) => {
    if (!idToken) return;
    setDetailLoading(true);
    setDetailError('');
    setDetail(null);
    setPointsDetail(null);
    try {
      // Customer detail comes back with balance summary baked in; the
      // ledger entries are a separate call so the detail dialog isn't
      // gated on them (rare large ledgers shouldn't stall the open).
      const [c, p] = await Promise.all([
        getCustomer(idToken, id),
        getCustomerPoints(idToken, id).catch((err) => {
          console.warn('admin points fetch failed (non-fatal):', err);
          return null;
        }),
      ]);
      setDetail(c);
      setPointsDetail(p);
    } catch (err) {
      setDetailError(
        err instanceof ApiError
          ? `API error ${err.status}`
          : (err as Error)?.message || 'fetch failed',
      );
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
        Customers
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
        Most recently active first.
      </Typography>

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          applySearch();
        }}
        sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Search email, name, or phone"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <Button
          type="submit"
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Search
        </Button>
        {search && (
          <Button
            onClick={clearSearch}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        )}
        {search && (
          <Typography variant="body2" sx={{ color: '#64748b', ml: 1 }}>
            matching “{search}”
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Orders</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Spent</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last order</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && data === null ? (
              <LoadingRow />
            ) : (data?.items.length ?? 0) === 0 ? (
              <EmptyRow text={search ? 'No customers match that search.' : 'No customers yet.'} />
            ) : (
              data!.items.map((c) => (
                <CustomerListRow key={c.id} c={c} onOpen={() => openDetail(c.id)} />
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={data?.total ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </TableContainer>

      <Dialog
        open={detail !== null || detailLoading || !!detailError}
        onClose={() => {
          setDetail(null);
          setDetailError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {detail?.display_name || detail?.email || (detailLoading ? 'Loading…' : 'Customer')}
            </Typography>
            {detail && (
              <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                {detail.id}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setDetail(null);
              setDetailError('');
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {detail && <CustomerDetailView c={detail} pointsDetail={pointsDetail} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const LoadingRow = () => (
  <TableRow>
    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, borderBottom: 'none' }}>
      <CircularProgress size={20} />
    </TableCell>
  </TableRow>
);

const EmptyRow = ({ text }: { text: string }) => (
  <TableRow>
    <TableCell
      colSpan={8}
      sx={{
        textAlign: 'center',
        color: '#94a3b8',
        py: 6,
        fontStyle: 'italic',
        borderBottom: 'none',
      }}
    >
      {text}
    </TableCell>
  </TableRow>
);

const CustomerListRow = ({ c, onOpen }: { c: CustomerRow; onOpen: () => void }) => {
  const name = c.display_name || (c.email ? c.email.split('@')[0] : '—');
  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ fontWeight: 500 }}>{name}</Box>
        {c.display_name && c.email && (
          <Box sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>{c.email}</Box>
        )}
      </TableCell>
      <TableCell>
        {c.email ?? <Box sx={{ color: '#94a3b8' }}>—</Box>}
        {c.email && !c.email_verified && (
          <Chip
            label="unverified"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: '0.7rem',
              backgroundColor: '#fef3c7',
              color: '#92400e',
            }}
          />
        )}
      </TableCell>
      <TableCell>{c.phone ?? <Box sx={{ color: '#94a3b8' }}>—</Box>}</TableCell>
      <TableCell sx={{ textAlign: 'right' }}>{c.order_count}</TableCell>
      <TableCell sx={{ textAlign: 'right' }}>{fmtMoney(c.total_spent)}</TableCell>
      <TableCell>{fmtDate(c.last_order_at)}</TableCell>
      <TableCell>{fmtDate(c.created_at)}</TableCell>
      <TableCell sx={{ textAlign: 'right' }}>
        <Button
          size="small"
          variant="text"
          onClick={onOpen}
          sx={{ textTransform: 'none' }}
        >
          View
        </Button>
      </TableCell>
    </TableRow>
  );
};

const formatAddressLines = (addr: CustomerAddress): string[] => {
  if (!addr) return [];
  const lines: string[] = [];
  const name = [addr.firstName, addr.lastName].filter(Boolean).join(' ').trim();
  if (name) lines.push(name);
  const street = [addr.street, addr.additional].filter(Boolean).join(', ').trim();
  if (street) lines.push(street);
  const cityLine = [
    addr.city,
    [addr.province, addr.postal].filter(Boolean).join(' ').trim(),
  ]
    .filter(Boolean)
    .join(', ')
    .trim();
  if (cityLine) lines.push(cityLine);
  if (addr.country) lines.push(addr.country);
  if (addr.phone) lines.push(addr.phone);
  return lines;
};

const AddressBlock = ({ label, addr }: { label: string; addr: CustomerAddress }) => {
  const lines = formatAddressLines(addr);
  return (
    <Box sx={{ minWidth: 200 }}>
      <Typography
        variant="caption"
        sx={{
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'block',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      {lines.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#cbd5e1', fontStyle: 'italic' }}>
          Not set
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </Typography>
      )}
    </Box>
  );
};

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}
    >
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const ledgerLabel = (e: PointsLedgerEntry): string => {
  switch (e.reason) {
    case 'purchase':
      return 'Order';
    case 'friend_code':
      return 'Friend referral';
    case 'redemption':
      return 'Redeemed';
    case 'refund_reversal':
      return 'Refund reversal';
    case 'adjustment':
      return 'Adjustment';
    default:
      return e.reason;
  }
};

const ledgerStatus = (e: PointsLedgerEntry): string => {
  if (e.redeemable_at === null) return 'pending';
  const ts = Date.parse(e.redeemable_at);
  if (Number.isFinite(ts) && ts > Date.now()) {
    return `redeemable ${fmtDate(e.redeemable_at)}`;
  }
  return 'available';
};

const CustomerDetailView = ({
  c,
  pointsDetail,
}: {
  c: CustomerDetail;
  pointsDetail: CustomerPointsResponse | null;
}) => (
  <Stack spacing={3}>
    <Stack direction="row" spacing={4} flexWrap="wrap">
      <Field label="Email" value={c.email ?? '—'} />
      <Field label="Phone" value={c.phone ?? '—'} />
      <Field label="Display name" value={c.display_name ?? '—'} />
      <Field label="Email verified" value={c.email_verified ? 'yes' : 'no'} />
      <Field label="Joined" value={fmtDateTime(c.created_at)} />
      <Field label="Updated" value={fmtDateTime(c.updated_at)} />
    </Stack>

    <Divider />

    <Stack direction="row" spacing={4} flexWrap="wrap" alignItems="flex-start">
      <Field
        label="Friend code"
        value={
          c.friend_code ? (
            <Box sx={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>
              {c.friend_code}
            </Box>
          ) : (
            <Box sx={{ color: '#cbd5e1', fontStyle: 'italic' }}>not requested</Box>
          )
        }
      />
      <Field
        label="Points available"
        value={`${c.points.available.toLocaleString()} pts ($${(c.points.available / 100).toFixed(2)})`}
      />
      <Field label="Points pending" value={`${c.points.pending.toLocaleString()} pts`} />
      <Field
        label="Lifetime earned"
        value={`${c.points.lifetime_earned.toLocaleString()} pts`}
      />
    </Stack>

    {pointsDetail && pointsDetail.recent.length > 0 && (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Recent points activity
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e5e7eb' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Delta</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>Order</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pointsDetail.recent.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{fmtDateTime(e.created_at)}</TableCell>
                  <TableCell>{ledgerLabel(e)}</TableCell>
                  <TableCell
                    sx={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: e.delta >= 0 ? '#15803d' : '#b91c1c',
                    }}
                  >
                    {e.delta >= 0 ? '+' : ''}
                    {e.delta.toLocaleString()}
                  </TableCell>
                  <TableCell>{ledgerStatus(e)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {e.order_id ? e.order_id.slice(0, 8) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )}

    <Divider />

    <Stack direction="row" spacing={4} flexWrap="wrap">
      <AddressBlock label="Default delivery" addr={c.default_delivery_address} />
      <AddressBlock label="Default billing" addr={c.default_billing_address} />
    </Stack>

    <Divider />

    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Recent orders
      </Typography>
      {c.recent_orders.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
          No orders yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e5e7eb' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>Order ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {c.recent_orders.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{fmtDateTime(o.created_at)}</TableCell>
                  <TableCell>{o.status}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{fmtMoney(o.total)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {o.id.slice(0, 8)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>

    <Box>
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
        Firebase UID:&nbsp;
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        {c.firebase_uid}
      </Typography>
    </Box>
  </Stack>
);

export default CustomersPage;
